import axios from "axios";
import { toast } from "react-toastify";
let redirectInProgress = false; // prevents multiple toasts/redirects

// Deliberate, actionable client errors that must reach the calling
// component instead of resolving into the 1s global toast (App.js
// autoClose={1000}) — that duration is unsuitable for an error the user
// must read and act on. Mirrors the case-400-only opt-out this set now
// replaces (originally `StatusNoteRequiredError` alone, CLNOTE-F003) and
// extends it to the checklist-comments error names (docs/TRD/
// checklist-comments.md §3.7, D6, CLCMT-F003). Every other 4xx in the app
// keeps today's behaviour byte-for-byte.
const INLINE_ERROR_NAMES = new Set([
  "StatusNoteRequiredError",
  "CommentInvalidError",
  "CommentForbiddenError",
  "CommentNotFoundError",
  "CommentStaleError",
  // Project merge rejections. Each is a deliberate, actionable 422 naming exactly what the
  // admin must fix, and MergeMappingInvalidError carries a details[] listing every problem at
  // once. Without these the 422 falls through to `default: return err`, which RESOLVES — so
  // the caller's .catch never runs, res.status !== 200, and the user sees nothing at all
  // after the modal has already dismissed itself.
  "MergeSourceNotFoundError",
  "MergeDestinationNotFoundError",
  "MergeSourceTypeMismatchError",
  "MergeDestinationTypeMismatchError",
  "MergeNoTimeEntriesError",
  "MergeMappingInvalidError",
  "MergeCheckListConflictError",
  "ProjectNotAccessibleError",
  // Notification bell (NOTIFY-F001, docs/TRD/task-notifications-contract.md §4). Same
  // resolve-and-swallow failure mode as the merge errors above: without these names, the
  // deliberate 4xx falls into the branch's toast-and-resolve path and the calling
  // component's `.catch` never runs. NotificationNotFoundError (404) covers the
  // contract's 404-not-403 rule for a row that is not the caller's, gone, or never
  // existed — indistinguishable on purpose, so it must always reject and let the
  // optimistic mark-read revert. TimeSheetTransitionForbiddenError (403) and
  // TimeSheetNotAccessibleError (404) are the F5 role-gate's two new failure modes on the
  // existing PUT /api/time_sheet/:id — without them a denied Approve/Return silently
  // appears to succeed, the worst outcome for a trigger whose whole point is trust.
  "NotificationNotFoundError",
  "TimeSheetTransitionForbiddenError",
  "TimeSheetNotAccessibleError",
  // Push subscribe/unsubscribe (NOTIFY-F007, contract §6, P1). A missing/relative/
  // non-https `endpoint` or missing `keys` is a 400 the UI must show as an actionable
  // "couldn't turn on push" message rather than the generic 400 toast — same
  // resolve-and-swallow failure mode as the errors above.
  "PushSubscriptionInvalidError",
  // Checklist rate import/reprice errors — an unregistered 4xx here would resolve instead of
  // reject, so the wizard's .catch would never run and the operator would see nothing.
  "RateSheetInvalidError",
  "RateImportRowsInvalidError",
  "RateImportForbiddenError",
  "RepriceScopeInvalidError",
  "RateRunFailedError",
]);
const makeApiCall = async (
  method,
  url,
  data = {},
  headers = {},
  responseType = ""
) => {
  const commonHeaders = {
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  };
  const headersWithContentType = {
    ...commonHeaders,
  };
  const config = {
    method,
    url,
    headers: headersWithContentType,
    data,
    responseType,
  };
  axios.interceptors.response.use(
    function (response) {
      // Do something with response data
      return response;
    },
    function (err) {
      const status = err.response?.status || 500;
      switch (status) {
        // authentication (token related issues)
        case 401: {
          // A wrong-password 401 from the login request itself is not a
          // session timeout — there is no session to expire and nothing to
          // redirect to (the user is already on the sign-in screen), so it
          // must not trigger the storage-clear/redirect flow below or reuse
          // its "session expired" wording.
          if (err.response?.data?.message === 'Invalid Credentials!') {
            return toast.error("Authentication failed. Invalid credentials.");
          }
            // Clear all stored data
          if (!redirectInProgress) {
            redirectInProgress = true;
            toast.error("Your session has expired. You’ll be redirected to sign in.");
            setTimeout(() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.href = "/";
                }, 4000);
          }
          return true;
        }
        // forbidden (permission related issues)
        case 403: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("forbidden permission");
        }

        // bad request
        case 400: {
          // Deliberate, actionable client errors are re-thrown so the calling
          // component can render them inline (originally the checklist
          // status-change note requirement alone, CLNOTE-F003; extended to
          // the checklist-comments names by CLCMT-F003 — see
          // INLINE_ERROR_NAMES above). Every other 400 keeps today's
          // behaviour byte-for-byte.
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Bad request");
        }

        // not found
        case 404: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Not Found");
        }

        // conflict
        case 409: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return toast.error("Conflict");
        }

        // unprocessable entity — the status every deliberate merge rejection uses
        case 422: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          // Unchanged from the default branch below for every other 422.
          return err;
        }

        default: {
          return err
        }

        // generic api error (server related) unexpected
        // case !422:
        // default: {
        //   return toast.error("forbidden permission");
        // }
      }
    }
  );

  return await axios.request(config);
};

// const makeApiCallCopy = async (
//   method,
//   url,
//   data = {},
//   headers = {},
//   responseType = ""
// ) => {
//   const commonHeaders = {
//     Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
//   };
//   const headersWithContentType = {
//     ...commonHeaders,
//   };
//   const config = {
//     method,
//     url,
//     headers: headersWithContentType,
//     data,
//     responseType,
//   };
//   axios.interceptors.response.use(
//     function (response) {
//       // Do something with response data
//       return response;
//     },
//     function (err) {
//       const status = err.response?.status || 500;
//       console.log("err", err);
//       switch (status) {
//         // authentication (token related issues)
//         case 401: {
//           return toast.error("Invalid authentication ");
//         }
//         // forbidden (permission related issues)
//         case 403: {
//           return toast.error("forbidden permission");
//         }

//         // bad request
//         case 400: {
//           return toast.error("Bad request");
//         }

//         // not found
//         case 404: {
//           return toast.error("Not Found");
//         }

//         // conflict
//         case 409: {
//           return toast.error("Conflict");
//         }

//         default: {
//           return err.response;
//         }

//         // generic api error (server related) unexpected
//         // case !422:
//         // default: {
//         //   return toast.error("forbidden permission");
//         // }
//       }
//     }
//   );

//   return await axios.request(config);
// };

const makeApiCallWithHeaders = async (method, url, data = {}, headers = {}) => {
  const config = {
    method,
    url,
    headers,
    data,
  };
  axios.interceptors.response.use(
    function (response) {
      // Do something with response data
      return response;
    },
    function (err) {
      const status = err.response?.status || 500;
      switch (status) {
        // authentication (token related issues)
        case 401: {
          return toast.error("Invalid authentication ");
        }
        // forbidden (permission related issues)
        case 403: {
          return toast.error("forbidden permission");
        }

        // bad request
        case 400: {
          return toast.error("Bad request");
        }

        // not found
        case 404: {
          return toast.error("Not Found");
        }

        // conflict
        case 409: {
          return toast.error("Conflict");
        }

        // unprocessable entity — mirrors the makeApiCall interceptor above. Both helpers
        // register their handler on the GLOBAL axios object, so whichever ran first in the
        // session handles a rejection; without this case a 422 falls through this switch,
        // which has no default, and the handler returns undefined — resolving the promise and
        // swallowing a deliberate error before the other interceptor can re-throw it.
        case 422: {
          if (INLINE_ERROR_NAMES.has(err.response?.data?.name)) {
            return Promise.reject(err);
          }
          return err;
        }

        // generic api error (server related) unexpected
        // default: {
        //   return toast.error("forbidden permission");
        // }
      }
    }
  );

  return await axios.request(config);
};

export { makeApiCall, makeApiCallWithHeaders };
